import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { usePayees } from '../../hooks/usePayees';
import { usePayeeAlerts } from '../../hooks/usePayeeAlerts';
import { usePlanLimits } from '../../hooks/usePlanLimits';
import { usePlanPermissions } from '../../hooks/usePlanPermissions';
import { PlanGate } from '../../components/feature/PlanGate';
import { PlanLimitAlert } from '../../components/feature/PlanLimitAlert';
import { UpgradeModal } from '../../components/feature/UpgradeModal';
import { PayeeModal } from './components/PayeeModal';
import type { Payee, PayeePayload } from '../../hooks/usePayees';

interface PayeeStat {
  id: string | null;
  name: string;
  totalSpent: number;
  count: number;
  lastDate: string | null;
  type: 'pf' | 'pj';
  is_active: boolean;
}

const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDate = (iso: string | null) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

export default function FavorecidosPage() {
  const { user } = useAuth();
  const { payees, loading, createPayee, updatePayee, deactivatePayee, reactivatePayee } = usePayees();
  const { alerts } = usePayeeAlerts();
  const { checkPayeeLimit } = usePlanLimits();
  const { canUsePayeesModule } = usePlanPermissions();
  const [showUpgrade, setShowUpgrade] = useState(false);
  const payeeLimit = checkPayeeLimit();

  const [stats, setStats] = useState<PayeeStat[]>([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Payee | null>(null);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<'ativos' | 'inativos' | 'todos'>('ativos');
  const [sortBy, setSortBy] = useState<'name' | 'total' | 'count'>('total');

  // Fetch spending stats per payee
  useEffect(() => {
    if (!user) return;
    const fetchStats = async () => {
      setStatsLoading(true);
      const { data } = await supabase
        .from('transactions')
        .select('payee_id, payee_name, amount, date')
        .eq('user_id', user.id)
        .eq('type', 'despesa')
        .is('deleted_at', null)
        .not('payee_name', 'is', null);

      const map: Record<string, { total: number; count: number; lastDate: string | null }> = {};
      (data ?? []).forEach((t: { payee_id: string | null; payee_name: string | null; amount: number; date: string }) => {
        const key = t.payee_name ?? '';
        if (!key) return;
        if (!map[key]) map[key] = { total: 0, count: 0, lastDate: null };
        map[key].total += Number(t.amount);
        map[key].count += 1;
        if (!map[key].lastDate || t.date > map[key].lastDate!) map[key].lastDate = t.date;
      });

      const result: PayeeStat[] = Object.entries(map).map(([name, s]) => {
        const payee = payees.find(p => p.name === name);
        return {
          id: payee?.id ?? null,
          name,
          totalSpent: s.total,
          count: s.count,
          lastDate: s.lastDate,
          type: payee?.type ?? 'pj',
          is_active: payee?.is_active ?? true,
        };
      });

      setStats(result);
      setStatsLoading(false);
    };
    fetchStats();
  }, [user, payees]);

  const handleSave = async (payload: PayeePayload) => {
    if (editing) return updatePayee(editing.id, payload);
    return createPayee(payload);
  };

  const openCreate = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (p: Payee) => { setEditing(p); setModalOpen(true); };

  const maxTotal = stats.length > 0 ? Math.max(...stats.map(s => s.totalSpent)) : 1;

  const filteredPayees = payees
    .filter(p => {
      if (tab === 'ativos') return p.is_active;
      if (tab === 'inativos') return !p.is_active;
      return true;
    })
    .filter(p => p.name.toLowerCase().includes(search.toLowerCase()) || (p.nickname ?? '').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      const sa = stats.find(s => s.id === a.id);
      const sb = stats.find(s => s.id === b.id);
      if (sortBy === 'total') return (sb?.totalSpent ?? 0) - (sa?.totalSpent ?? 0);
      return (sb?.count ?? 0) - (sa?.count ?? 0);
    });

  const totalSpentAll = stats.reduce((s, p) => s + p.totalSpent, 0);
  const topPayee = stats.sort((a, b) => b.totalSpent - a.totalSpent)[0];

  return (
    <PlanGate feature="canUsePayeesModule" mode="page" title="Módulo de Favorecidos" description="Cadastre favorecidos, acompanhe gastos por pessoa/empresa e use o auto-preenchimento inteligente no lançamento.">
    <div className="space-y-5 w-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-700 text-gray-900">Favorecidos</h1>
          <p className="text-sm text-gray-500 mt-0.5">Gerencie para quem você paga e acompanhe seus gastos</p>
        </div>
        <button
          onClick={payeeLimit.allowed ? openCreate : () => setShowUpgrade(true)}
          className={`flex items-center gap-2 px-4 py-2.5 text-white text-sm font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap ${payeeLimit.allowed ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-gray-300 hover:bg-gray-400'}`}>
          <i className={payeeLimit.allowed ? 'ri-add-line' : 'ri-lock-line'} />
          {payeeLimit.allowed ? 'Novo Favorecido' : `Limite atingido (${payeeLimit.current}/${payeeLimit.limit})`}
        </button>
      </div>

      {/* Plan limit alert */}
      <PlanLimitAlert
        current={payeeLimit.current}
        limit={payeeLimit.limit}
        resource="favorecidos"
        onUpgrade={() => setShowUpgrade(true)}
      />

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-700 text-gray-600 uppercase tracking-wide flex items-center gap-2">
            <i className="ri-alert-line text-amber-500" />Alertas Inteligentes
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {alerts.slice(0, 4).map(alert => (
              <div key={alert.id} className={`flex items-start gap-3 px-4 py-3 rounded-xl border ${
                alert.priority === 'high' ? 'bg-red-50 border-red-100' :
                alert.priority === 'medium' ? 'bg-amber-50 border-amber-100' : 'bg-gray-50 border-gray-100'
              }`}>
                <i className={`text-base shrink-0 mt-0.5 ${
                  alert.type === 'increase' ? 'ri-trending-up-line text-red-500' :
                  alert.type === 'recurring' ? 'ri-repeat-line text-emerald-500' : 'ri-error-warning-line text-amber-500'
                }`} />
                <p className={`text-xs font-500 leading-relaxed ${
                  alert.priority === 'high' ? 'text-red-700' :
                  alert.priority === 'medium' ? 'text-amber-700' : 'text-gray-600'
                }`}>{alert.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total de Favorecidos', value: String(payees.filter(p => p.is_active).length), icon: 'ri-user-heart-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'Total Gasto', value: fmt(totalSpentAll), icon: 'ri-money-dollar-circle-line', color: 'text-red-500', bg: 'bg-red-50' },
          { label: 'Maior Gasto', value: topPayee?.name ?? '—', icon: 'ri-trophy-line', color: 'text-amber-500', bg: 'bg-amber-50' },
          { label: 'Alertas Ativos', value: String(alerts.length), icon: 'ri-alert-line', color: 'text-orange-500', bg: 'bg-orange-50' },
        ].map((card, i) => (
          <div key={i} className="bg-white rounded-2xl border border-gray-100 p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${card.bg}`}>
                <i className={`${card.icon} ${card.color} text-lg`} />
              </div>
              <span className="text-xs text-gray-500 font-500 flex-1 leading-tight">{card.label}</span>
            </div>
            <p className="text-base font-700 text-gray-900 truncate">{card.value}</p>
          </div>
        ))}
      </div>

      {/* Ranking */}
      {stats.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h2 className="text-[15px] font-700 text-gray-900 mb-4">Ranking de Gastos por Favorecido</h2>
          {statsLoading ? (
            <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-10 bg-gray-50 rounded-xl animate-pulse" />)}</div>
          ) : (
            <div className="space-y-3">
              {stats.sort((a, b) => b.totalSpent - a.totalSpent).slice(0, 8).map((s, i) => (
                <div key={s.name} className="flex items-center gap-3">
                  <span className="text-xs font-700 text-gray-400 w-5 text-right shrink-0">{i + 1}</span>
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-emerald-100 shrink-0">
                    <i className={`${s.type === 'pf' ? 'ri-user-line' : 'ri-building-line'} text-emerald-600 text-xs`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-600 text-gray-800 truncate">{s.name}</span>
                      <span className="text-sm font-700 text-red-600 ml-2 shrink-0">{fmt(s.totalSpent)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-400 rounded-full transition-all"
                          style={{ width: `${Math.round((s.totalSpent / maxTotal) * 100)}%` }} />
                      </div>
                      <span className="text-xs text-gray-400 shrink-0">{s.count} lanç.</span>
                      {s.lastDate && <span className="text-xs text-gray-400 shrink-0">Último: {fmtDate(s.lastDate)}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* List */}
      <div className="bg-white rounded-2xl border border-gray-100">
        <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
            {(['ativos', 'inativos', 'todos'] as const).map(t => (
              <button key={t} onClick={() => setTab(t)}
                className={`px-3 py-1.5 text-xs font-600 rounded-lg transition-all cursor-pointer whitespace-nowrap ${tab === t ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-1">
            <div className="relative flex-1 max-w-xs">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Buscar favorecido..." className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-emerald-400" />
            </div>
            <select value={sortBy} onChange={e => setSortBy(e.target.value as typeof sortBy)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none cursor-pointer bg-white">
              <option value="total">Maior gasto</option>
              <option value="count">Mais lançamentos</option>
              <option value="name">Nome A-Z</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="p-6 space-y-3">{[1,2,3,4].map(i => <div key={i} className="h-14 bg-gray-50 rounded-xl animate-pulse" />)}</div>
        ) : filteredPayees.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gray-50">
              <i className="ri-user-heart-line text-3xl text-gray-200" />
            </div>
            <p className="text-sm text-gray-400">Nenhum favorecido cadastrado ainda</p>
            <button onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 text-sm font-600 rounded-xl cursor-pointer hover:bg-emerald-100 whitespace-nowrap">
              <i className="ri-add-line" />Criar primeiro favorecido
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {filteredPayees.map(p => {
              const stat = stats.find(s => s.id === p.id);
              return (
                <div key={p.id} className={`flex items-center gap-4 px-5 py-4 hover:bg-gray-50/50 transition-colors ${!p.is_active ? 'opacity-60' : ''}`}>
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-100 shrink-0">
                    <i className={`${p.type === 'pf' ? 'ri-user-line' : 'ri-building-line'} text-emerald-600 text-base`} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-700 text-gray-900 truncate">{p.name}</p>
                      {!p.is_active && <span className="text-[10px] font-600 px-1.5 py-0.5 bg-gray-100 text-gray-400 rounded-full shrink-0">Inativo</span>}
                      {p.nickname && <span className="text-xs text-gray-400 truncate">{p.nickname}</span>}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                      <span className="text-xs text-gray-400">{p.type === 'pf' ? 'Pessoa Física' : 'Pessoa Jurídica'}</span>
                      {p.default_category && (
                        <span className="text-[10px] font-600 px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded-full flex items-center gap-0.5 shrink-0">
                          <i className="ri-price-tag-3-line" />{p.default_category}
                          {p.default_subcategory && ` · ${p.default_subcategory}`}
                        </span>
                      )}
                      {stat && <span className="text-xs text-gray-400">{stat.count} lançamento{stat.count !== 1 ? 's' : ''}</span>}
                    </div>
                  </div>

                  {stat && (
                    <div className="text-right shrink-0">
                      <p className="text-sm font-700 text-red-600">{fmt(stat.totalSpent)}</p>
                      <p className="text-xs text-gray-400">Último: {fmtDate(stat.lastDate)}</p>
                    </div>
                  )}

                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => openEdit(p)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
                      <i className="ri-edit-line text-sm" />
                    </button>
                    <button
                      onClick={() => {
                        if (p.is_active) {
                          deactivatePayee(p.id);
                        } else {
                          // Check payee limit before reactivating — same rule as creating
                          const limitCheck = checkPayeeLimit();
                          if (!limitCheck.allowed) {
                            setShowUpgrade(true);
                          } else {
                            reactivatePayee(p.id);
                          }
                        }
                      }}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer"
                      title={p.is_active ? 'Desativar' : 'Reativar'}
                    >
                      <i className={`text-sm ${p.is_active ? 'ri-eye-off-line' : 'ri-eye-line'}`} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <PayeeModal
        open={modalOpen}
        payee={editing}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSave={handleSave as (p: PayeePayload) => Promise<{ success: boolean; message: string }>}
      />

      <UpgradeModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        title="Desbloquear: Mais Favorecidos"
        description="Faça upgrade para cadastrar favorecidos ilimitados e usar o auto-preenchimento inteligente."
      />
    </div>
    </PlanGate>
  );
}
